import * as THREE from 'three';
import { ClipTask, ElevationGradientRepeat, PointSizeType } from './defines';
import { PointCloudTree } from './PointCloudTree';
import { debug } from './potreeState';

const measureTimings = false;

/** WebGL rendering context type */
type GL = WebGL2RenderingContext;

/** WebGL extension map - initialized lazily from a GL context */
let extensions: {
  get(name: string): Record<string, number> | null;
};

/** Attribute location entry */
interface AttributeLocationEntry {
  name: string;
  location: number;
}

/** Attribute locations map type */
type AttributeLocationsMap = Record<string, AttributeLocationEntry>;

/** Uniform entry for shader cache */
interface UniformEntry {
  location: WebGLUniformLocation | null;
  value: unknown;
}

/** Uniform block entry */
interface UniformBlockEntry {
  name: string;
  index: number;
  dataSize: number;
  buffer: WebGLBuffer | null;
}

/** VBO entry for buffer */
interface VBOEntry {
  handle: WebGLBuffer | null;
  name: string;
  count: number;
  itemSize: number;
  type: unknown;
  version: number;
}

/** Shader cache entry */
interface ShaderCacheEntry {
  program: WebGLProgram | null;
  vs: WebGLShader | null;
  fs: WebGLShader | null;
  attributeLocations: Record<string, number>;
  uniformLocations: Record<string, WebGLUniformLocation | null>;
  uniforms: Record<string, UniformEntry>;
  uniformBlocks: Record<string, UniformBlockEntry>;
}

/** PointCloudMaterial-like interface for renderer */
interface PotreeMaterial {
  pointSizeType: number;
  activeAttributeName: string | null;
  visibleNodesTexture: THREE.DataTexture;
  vertexShader: string;
  fragmentShader: string;
  needsUpdate: boolean;
  snapEnabled?: boolean;
  numSnapshots?: number;
  clipBoxes: Array<{
    inverse: THREE.Matrix4;
    box: {
      getActualClipTask(): number;
      color: THREE.Color;
      useIntersection?: boolean;
    };
  }>;
  clipSpheres: Array<{
    inverse: THREE.Matrix4;
    sphere: {
      getActualClipTask(): number;
    };
  }>;
  clipPolygons: Array<{
    viewMatrix: THREE.Matrix4;
    projMatrix: THREE.Matrix4;
    markers: Array<{ position: THREE.Vector3 }>;
  }>;
  clipPlanes: Array<{
    inverse: THREE.Matrix4;
    plane: {
      actualClipTask: number;
      tolerance: number;
      gradient: string;
      heatmapIsolate?: boolean;
      useIntersection?: boolean;
      identifier?: string;
    };
  }>;
  clusteredSegments: Array<{
    identifier: number;
    color: THREE.Color;
    visible: boolean;
    clipTask: number;
    classification: number;
  }>;
  uniforms: Record<string, { type?: string; value: unknown }>;
  spacing: number;
  size: number;
  color: THREE.Color;
  opacity: number;
  densityMode?: boolean;
  /** Per-point alpha for density mode; see PointCloudMaterial.densityAlpha. */
  densityAlpha: number;
  elevationRange: [number, number];
  intensityRange: [number, number];
  intensityGamma: number;
  intensityBrightness: number;
  intensityContrast: number;
  rgbGamma: number;
  rgbBrightness: number;
  rgbContrast: number;
  transition: number;
  weightRGB: number;
  weightIntensity: number;
  weightElevation: number;
  weightClassification: number;
  weightReturnNumber: number;
  weightSourceID: number;
  elevationGradientRepeat: number;
  gradientTexture: THREE.Texture;
  gradientTextures?: Record<string, THREE.Texture>;
  classificationTexture: THREE.Texture;
  matcapTexture: THREE.Texture;
  screenWidth: number;
  screenHeight: number;
  fov: number;
  near: number;
  far: number;
  shape: number;
  depthTest: boolean;
  depthWrite: boolean;
  classification: unknown;
  recomputeClassification(): void;
  heightMin: number;
  heightMax: number;
  intensityMin: number;
  intensityMax: number;
  isolatedClipSegments(): PotreeMaterial['clusteredSegments'];
  isolatedClipBoxes(): PotreeMaterial['clipBoxes'];
  isolatedClipPlanes(): PotreeMaterial['clipPlanes'];
  setClipBoxes(boxes: PotreeMaterial['clipBoxes']): void;
  setClusteredSegments(segments: PotreeMaterial['clusteredSegments']): void;
  setClipPlanes(planes: PotreeMaterial['clipPlanes']): void;
  sources?: Record<string, { color: [number, number, number]; visible: boolean }>;
  getRange(name: string): [number, number] | null;
}

/** Octree-like interface for renderer */
interface PotreeOctree {
  material: PotreeMaterial;
  pcoGeometry: {
    spacing: number;
    boundingBox: THREE.Box3;
    root: {
      isLoaded(): boolean;
      geometry: THREE.BufferGeometry | null;
    };
    pointAttributes: {
      attributes: Array<{
        name: string;
        range: [number, number];
        initialRange: [number, number];
      }>;
    };
  };
  visibleNodes: PotreeOctreeNode[];
  computeVisibilityTextureData(
    nodes: PotreeOctreeNode[],
    camera: THREE.Camera,
  ): {
    data: Uint8Array;
    offsets: Map<PotreeOctreeNode, number>;
  };
  getAttribute(name: string): {
    initialRange: [number, number];
    range: [number, number];
  } | null;
}

/** Node-like interface for renderer */
interface PotreeOctreeNode {
  name: string;
  sceneNode: THREE.Object3D;
  geometryNode: {
    geometry: THREE.BufferGeometry | null;
    estimatedSpacing?: number;
    density?: number;
  };
  getLevel(): number | null;
  debug?: boolean;
  refinedByAllChildren?: boolean;
}

/** Render params interface */
interface RenderParams {
  material?: PotreeMaterial;
  shadowMaps?: Array<{
    target: THREE.WebGLRenderTarget;
    camera: THREE.Camera;
  }>;
  viewOverride?: THREE.Matrix4;
  vnTextureNodes?: PotreeOctreeNode[];
  clipSpheres?: THREE.Object3D[];
  transparent?: boolean;
  blendFunc?: [number, number];
  depthTest?: boolean;
  depthWrite?: boolean;
  orderedIdentifiers?: Array<{ identifier: string; index: number }>;
}

// Copied from three.js: WebGLRenderer.js
function paramThreeToGL(_gl: GL, p: number): number {
  if (p === THREE.RepeatWrapping) return _gl.REPEAT;
  if (p === THREE.ClampToEdgeWrapping) return _gl.CLAMP_TO_EDGE;
  if (p === THREE.MirroredRepeatWrapping) return _gl.MIRRORED_REPEAT;

  if (p === THREE.NearestFilter) return _gl.NEAREST;
  if (p === THREE.NearestMipMapNearestFilter) return _gl.NEAREST_MIPMAP_NEAREST;
  if (p === THREE.NearestMipMapLinearFilter) return _gl.NEAREST_MIPMAP_LINEAR;

  if (p === THREE.LinearFilter) return _gl.LINEAR;
  if (p === THREE.LinearMipMapNearestFilter) return _gl.LINEAR_MIPMAP_NEAREST;
  if (p === THREE.LinearMipMapLinearFilter) return _gl.LINEAR_MIPMAP_LINEAR;

  if (p === THREE.UnsignedByteType) return _gl.UNSIGNED_BYTE;
  if (p === THREE.UnsignedShort4444Type) return _gl.UNSIGNED_SHORT_4_4_4_4;
  if (p === THREE.UnsignedShort5551Type) return _gl.UNSIGNED_SHORT_5_5_5_1;

  if (p === THREE.ByteType) return _gl.BYTE;
  if (p === THREE.ShortType) return _gl.SHORT;
  if (p === THREE.UnsignedShortType) return _gl.UNSIGNED_SHORT;
  if (p === THREE.IntType) return _gl.INT;
  if (p === THREE.UnsignedIntType) return _gl.UNSIGNED_INT;
  if (p === THREE.FloatType) return _gl.FLOAT;

  if (p === THREE.HalfFloatType) {
    const ext = extensions.get('OES_texture_half_float');
    if (ext !== null) return ext.HALF_FLOAT_OES;
  }

  if (p === THREE.AlphaFormat) return _gl.ALPHA;
  // LuminanceFormat and LuminanceAlphaFormat removed in newer Three.js
  if (p === 1024) return _gl.LUMINANCE; // THREE.LuminanceFormat value
  if (p === 1025) return _gl.LUMINANCE_ALPHA; // THREE.LuminanceAlphaFormat value
  if (p === THREE.RGBAFormat) return _gl.RGBA;
  if (p === THREE.DepthFormat) return _gl.DEPTH_COMPONENT;
  if (p === THREE.DepthStencilFormat) return _gl.DEPTH_STENCIL;

  if (p === THREE.AddEquation) return _gl.FUNC_ADD;
  if (p === THREE.SubtractEquation) return _gl.FUNC_SUBTRACT;
  if (p === THREE.ReverseSubtractEquation) return _gl.FUNC_REVERSE_SUBTRACT;

  if (p === THREE.ZeroFactor) return _gl.ZERO;
  if (p === THREE.OneFactor) return _gl.ONE;
  if (p === THREE.SrcColorFactor) return _gl.SRC_COLOR;
  if (p === THREE.OneMinusSrcColorFactor) return _gl.ONE_MINUS_SRC_COLOR;
  if (p === THREE.SrcAlphaFactor) return _gl.SRC_ALPHA;
  if (p === THREE.OneMinusSrcAlphaFactor) return _gl.ONE_MINUS_SRC_ALPHA;
  if (p === THREE.DstAlphaFactor) return _gl.DST_ALPHA;
  if (p === THREE.OneMinusDstAlphaFactor) return _gl.ONE_MINUS_DST_ALPHA;

  if (p === THREE.DstColorFactor) return _gl.DST_COLOR;
  if (p === THREE.OneMinusDstColorFactor) return _gl.ONE_MINUS_DST_COLOR;
  if (p === THREE.SrcAlphaSaturateFactor) return _gl.SRC_ALPHA_SATURATE;

  if (p === THREE.RGB_S3TC_DXT1_Format || p === THREE.RGBA_S3TC_DXT1_Format || p === THREE.RGBA_S3TC_DXT3_Format || p === THREE.RGBA_S3TC_DXT5_Format) {
    const ext = extensions.get('WEBGL_compressed_texture_s3tc');

    if (ext !== null) {
      if (p === THREE.RGB_S3TC_DXT1_Format) return ext.COMPRESSED_RGB_S3TC_DXT1_EXT;
      if (p === THREE.RGBA_S3TC_DXT1_Format) return ext.COMPRESSED_RGBA_S3TC_DXT1_EXT;
      if (p === THREE.RGBA_S3TC_DXT3_Format) return ext.COMPRESSED_RGBA_S3TC_DXT3_EXT;
      if (p === THREE.RGBA_S3TC_DXT5_Format) return ext.COMPRESSED_RGBA_S3TC_DXT5_EXT;
    }
  }

  if (p === THREE.RGB_PVRTC_4BPPV1_Format || p === THREE.RGB_PVRTC_2BPPV1_Format || p === THREE.RGBA_PVRTC_4BPPV1_Format || p === THREE.RGBA_PVRTC_2BPPV1_Format) {
    const ext = extensions.get('WEBGL_compressed_texture_pvrtc');

    if (ext !== null) {
      if (p === THREE.RGB_PVRTC_4BPPV1_Format) return ext.COMPRESSED_RGB_PVRTC_4BPPV1_IMG;
      if (p === THREE.RGB_PVRTC_2BPPV1_Format) return ext.COMPRESSED_RGB_PVRTC_2BPPV1_IMG;
      if (p === THREE.RGBA_PVRTC_4BPPV1_Format) return ext.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG;
      if (p === THREE.RGBA_PVRTC_2BPPV1_Format) return ext.COMPRESSED_RGBA_PVRTC_2BPPV1_IMG;
    }
  }

  if (p === THREE.RGB_ETC1_Format) {
    const ext = extensions.get('WEBGL_compressed_texture_etc1');

    if (ext !== null) return ext.COMPRESSED_RGB_ETC1_WEBGL;
  }

  if (p === THREE.MinEquation || p === THREE.MaxEquation) {
    const ext = extensions.get('EXT_blend_minmax');

    if (ext !== null) {
      if (p === THREE.MinEquation) return ext.MIN_EXT;
      if (p === THREE.MaxEquation) return ext.MAX_EXT;
    }
  }

  // UnsignedInt248Type = 1020
  if (p === 1020) {
    const ext = extensions.get('WEBGL_depth_texture');

    if (ext !== null) return ext.UNSIGNED_INT_24_8_WEBGL;
  }

  return 0;
}

const attributeLocations: AttributeLocationsMap = {
  position: { name: 'position', location: 0 },
  color: { name: 'color', location: 1 },
  rgba: { name: 'color', location: 1 },
  intensity: { name: 'intensity', location: 2 },
  classification: { name: 'classification', location: 3 },
  returnNumber: { name: 'returnNumber', location: 4 },
  'return number': { name: 'returnNumber', location: 4 },
  returns: { name: 'returnNumber', location: 4 },
  numberOfReturns: { name: 'numberOfReturns', location: 5 },
  'number of returns': { name: 'numberOfReturns', location: 5 },
  pointSourceID: { name: 'pointSourceID', location: 6 },
  'source id': { name: 'pointSourceID', location: 6 },
  'point source id': { name: 'pointSourceID', location: 6 },
  indices: { name: 'indices', location: 7 },
  normal: { name: 'normal', location: 8 },
  spacing: { name: 'spacing', location: 9 },
  'gps-time': { name: 'gpsTime', location: 10 },
  aExtra: { name: 'aExtra', location: 11 },
  segment: { name: 'segment', location: 12 },
  source: { name: 'source', location: 13 },
};

class Shader {
  gl: GL;
  name: string;
  vsSource: string;
  fsSource: string;
  cache: Map<string, ShaderCacheEntry>;
  vs: WebGLShader | null;
  fs: WebGLShader | null;
  program: WebGLProgram | null;
  uniformLocations: Record<string, WebGLUniformLocation | null>;
  attributeLocations: Record<string, number>;
  uniformBlockIndices: Record<string, number>;
  uniformBlocks: Record<string, UniformBlockEntry>;
  uniforms: Record<string, UniformEntry>;

  constructor(gl: GL, name: string, vsSource: string, fsSource: string) {
    this.gl = gl;
    this.name = name;
    this.vsSource = vsSource;
    this.fsSource = fsSource;

    this.cache = new Map();

    this.vs = null;
    this.fs = null;
    this.program = null;

    this.uniformLocations = {};
    this.attributeLocations = {};
    this.uniformBlockIndices = {};
    this.uniformBlocks = {};
    this.uniforms = {};

    this.update(vsSource, fsSource);
  }

  update(vsSource: string, fsSource: string) {
    this.vsSource = vsSource;
    this.fsSource = fsSource;

    this.linkProgram();
  }

  compileShader(shader: WebGLShader, source: string) {
    const gl = this.gl;

    gl.shaderSource(shader, source);

    gl.compileShader(shader);

    const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
    if (!success) {
      const info = gl.getShaderInfoLog(shader);
      const numberedSource = source
        .split('\n')
        .map((a: string, i: number) => `${i + 1}`.padEnd(5) + a)
        .join('\n');
      throw new Error(`could not compile shader ${this.name}: ${info}, \n${numberedSource}`);
    }
  }

  linkProgram() {
    const gl = this.gl;

    this.uniformLocations = {};
    this.attributeLocations = {};
    this.uniforms = {};

    gl.useProgram(null);

    const cached = this.cache.get(`${this.vsSource}, ${this.fsSource}`);
    if (cached) {
      this.program = cached.program;
      this.vs = cached.vs;
      this.fs = cached.fs;
      this.attributeLocations = cached.attributeLocations;
      this.uniformLocations = cached.uniformLocations;
      this.uniformBlocks = cached.uniformBlocks;
      this.uniforms = cached.uniforms;

      return;
    } else {
      this.vs = gl.createShader(gl.VERTEX_SHADER);
      this.fs = gl.createShader(gl.FRAGMENT_SHADER);
      this.program = gl.createProgram();

      if (!this.vs || !this.fs || !this.program) {
        throw new Error(`Failed to create shader/program for ${this.name}`);
      }

      for (const name of Object.keys(attributeLocations)) {
        const loc = attributeLocations[name].location;
        const glslName = attributeLocations[name].name;
        gl.bindAttribLocation(this.program, loc, glslName);
      }

      this.compileShader(this.vs, this.vsSource);
      this.compileShader(this.fs, this.fsSource);

      const program = this.program;

      gl.attachShader(program, this.vs);
      gl.attachShader(program, this.fs);

      gl.linkProgram(program);

      gl.detachShader(program, this.vs);
      gl.detachShader(program, this.fs);

      const success = gl.getProgramParameter(program, gl.LINK_STATUS);
      if (!success) {
        const info = gl.getProgramInfoLog(program);
        throw new Error(`could not link program ${this.name}: ${info}`);
      }

      {
        // attribute locations
        const numAttributes = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES) as number;

        for (let i = 0; i < numAttributes; i++) {
          const attribute = gl.getActiveAttrib(program, i);
          if (!attribute) continue;

          const location = gl.getAttribLocation(program, attribute.name);

          this.attributeLocations[attribute.name] = location;
        }
      }

      {
        // uniform locations
        const numUniforms = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;

        for (let i = 0; i < numUniforms; i++) {
          const uniform = gl.getActiveUniform(program, i);
          if (!uniform) continue;

          const location = gl.getUniformLocation(program, uniform.name);

          this.uniformLocations[uniform.name] = location;
          this.uniforms[uniform.name] = {
            location: location,
            value: null,
          };
        }
      }

      // uniform blocks
      if (gl instanceof WebGL2RenderingContext) {
        const numBlocks = gl.getProgramParameter(program, gl.ACTIVE_UNIFORM_BLOCKS) as number;

        for (let i = 0; i < numBlocks; i++) {
          const blockName = gl.getActiveUniformBlockName(program, i);
          if (!blockName) continue;

          const blockIndex = gl.getUniformBlockIndex(program, blockName);

          this.uniformBlockIndices[blockName] = blockIndex;

          gl.uniformBlockBinding(program, blockIndex, blockIndex);
          const dataSize = gl.getActiveUniformBlockParameter(program, blockIndex, gl.UNIFORM_BLOCK_DATA_SIZE) as number;

          const uBuffer = gl.createBuffer();
          gl.bindBuffer(gl.UNIFORM_BUFFER, uBuffer);
          gl.bufferData(gl.UNIFORM_BUFFER, dataSize, gl.DYNAMIC_READ);

          gl.bindBufferBase(gl.UNIFORM_BUFFER, blockIndex, uBuffer);

          gl.bindBuffer(gl.UNIFORM_BUFFER, null);

          this.uniformBlocks[blockName] = {
            name: blockName,
            index: blockIndex,
            dataSize: dataSize,
            buffer: uBuffer,
          };
        }
      }

      const cacheEntry: ShaderCacheEntry = {
        program: this.program,
        vs: this.vs,
        fs: this.fs,
        attributeLocations: this.attributeLocations,
        uniformLocations: this.uniformLocations,
        uniforms: this.uniforms,
        uniformBlocks: this.uniformBlocks,
      };

      this.cache.set(`${this.vsSource}, ${this.fsSource}`, cacheEntry);
    }
  }

  setUniformMatrix4(name: string, value: THREE.Matrix4) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    const tmp = new Float32Array(value.elements);
    gl.uniformMatrix4fv(location, false, tmp);
  }

  setUniform1f(name: string, value: number) {
    const gl = this.gl;
    const uniform = this.uniforms[name];

    if (uniform === undefined) {
      return;
    }

    if (uniform.value === value) {
      return;
    }

    uniform.value = value;

    gl.uniform1f(uniform.location, value);
  }

  setUniformBoolean(name: string, value: boolean) {
    const gl = this.gl;
    const uniform = this.uniforms[name];

    if (uniform === undefined) {
      return;
    }

    if (uniform.value === value) {
      return;
    }

    uniform.value = value;

    gl.uniform1i(uniform.location, value ? 1 : 0);
  }

  setUniformTexture(name: string, value: number) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform1i(location, value);
  }

  setUniform2f(name: string, value: number[]) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform2f(location, value[0], value[1]);
  }

  setUniform3f(name: string, value: number[]) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform3f(location, value[0], value[1], value[2]);
  }

  setUniform(name: string, value: THREE.Matrix4 | number | boolean | PotreeWebGLTexture | number[]) {
    if (value instanceof THREE.Matrix4) {
      this.setUniformMatrix4(name, value);
    } else if (typeof value === 'number') {
      this.setUniform1f(name, value);
    } else if (typeof value === 'boolean') {
      this.setUniformBoolean(name, value);
    } else if (value instanceof PotreeWebGLTexture) {
      this.setUniformTexture(name, value as unknown as number);
    } else if (value instanceof Array) {
      if (value.length === 2) {
        this.setUniform2f(name, value);
      } else if (value.length === 3) {
        this.setUniform3f(name, value);
      }
    } else {
      console.error('unhandled uniform type: ', name, value);
    }
  }

  setUniform1i(name: string, value: number) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform1i(location, value);
  }
}

class PotreeWebGLTexture {
  gl: GL;
  texture: THREE.Texture;
  id: globalThis.WebGLTexture | null;
  target: number;
  version: number;

  constructor(gl: GL, texture: THREE.Texture) {
    this.gl = gl;

    this.texture = texture;
    this.id = gl.createTexture();

    this.target = gl.TEXTURE_2D;
    this.version = -1;

    this.update();
  }

  update() {
    if (!this.texture.image) {
      this.version = this.texture.version;

      return;
    }

    const gl = this.gl;
    const texture = this.texture;

    if (this.version === texture.version) {
      return;
    }

    this.target = gl.TEXTURE_2D;

    gl.bindTexture(this.target, this.id);

    const level = 0;
    const internalFormat = paramThreeToGL(gl, texture.format);
    const textureImage = texture.image as { width: number; height: number; data?: ArrayBufferView };
    const width = textureImage.width;
    const height = textureImage.height;
    const border = 0;
    const srcFormat = internalFormat;
    const srcType = paramThreeToGL(gl, texture.type);

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, texture.flipY ? 1 : 0);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, texture.premultiplyAlpha ? 1 : 0);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, texture.unpackAlignment);

    if (texture instanceof THREE.DataTexture) {
      const data = texture.image.data as ArrayBufferView | null;

      gl.texParameteri(this.target, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(this.target, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      gl.texParameteri(this.target, gl.TEXTURE_MAG_FILTER, paramThreeToGL(gl, texture.magFilter));
      gl.texParameteri(this.target, gl.TEXTURE_MIN_FILTER, paramThreeToGL(gl, texture.minFilter));

      // Handle explicit internalFormat for float textures (WebGL2)
      const texInternalFormat = (texture as unknown as Record<string, unknown>).internalFormat as string | undefined;
      let glInternalFormat = internalFormat;
      if (texInternalFormat === 'RGBA32F') {
        glInternalFormat = gl.RGBA32F;
      } else if (texInternalFormat === 'RGBA16F') {
        glInternalFormat = gl.RGBA16F;
      }

      gl.texImage2D(this.target, level, glInternalFormat, width, height, border, srcFormat, srcType, data);
    } else if (texture instanceof THREE.CanvasTexture || texture instanceof THREE.Texture) {
      const data: TexImageSource = texture.image;

      gl.texParameteri(this.target, gl.TEXTURE_WRAP_S, paramThreeToGL(gl, texture.wrapS));
      gl.texParameteri(this.target, gl.TEXTURE_WRAP_T, paramThreeToGL(gl, texture.wrapT));

      gl.texParameteri(this.target, gl.TEXTURE_MAG_FILTER, paramThreeToGL(gl, texture.magFilter));
      gl.texParameteri(this.target, gl.TEXTURE_MIN_FILTER, paramThreeToGL(gl, texture.minFilter));

      gl.texImage2D(this.target, level, internalFormat, internalFormat, srcType, data as TexImageSource);

      if (texture instanceof THREE.Texture) {
        gl.generateMipmap(gl.TEXTURE_2D);
      }
    }

    gl.bindTexture(this.target, null);

    this.version = texture.version;
  }
}

class PotreeWebGLBuffer {
  numElements: number;
  vao: WebGLVertexArrayObject | null;
  vbos: Map<string, VBOEntry>;

  constructor() {
    this.numElements = 0;
    this.vao = null;
    this.vbos = new Map();
  }
}

export class Renderer {
  threeRenderer: THREE.WebGLRenderer;
  gl: GL;
  buffers: Map<THREE.BufferGeometry, PotreeWebGLBuffer>;
  shaders: Map<PotreeMaterial, Shader>;
  textures: Map<THREE.Texture, PotreeWebGLTexture>;
  glTypeMapping: Map<unknown, number>;
  toggle: number;

  constructor(threeRenderer: THREE.WebGLRenderer) {
    this.threeRenderer = threeRenderer;
    this.gl = this.threeRenderer.getContext() as GL;

    // Initialize extensions helper
    if (!extensions) {
      const gl = this.gl;
      extensions = {
        get(name: string) {
          return gl.getExtension(name) as Record<string, number> | null;
        },
      };
    }

    this.buffers = new Map();
    this.shaders = new Map();
    this.textures = new Map();

    this.glTypeMapping = new Map();
    this.glTypeMapping.set(Float32Array, this.gl.FLOAT);
    this.glTypeMapping.set(Uint8Array, this.gl.UNSIGNED_BYTE);
    this.glTypeMapping.set(Uint16Array, this.gl.UNSIGNED_SHORT);

    this.toggle = 0;
  }

  deleteBuffer(geometry: THREE.BufferGeometry) {
    const gl = this.gl;
    const webglBuffer = this.buffers.get(geometry);
    if (webglBuffer != null) {
      for (const attributeName in geometry.attributes) {
        const vbo = webglBuffer.vbos.get(attributeName);
        if (vbo) gl.deleteBuffer(vbo.handle);
      }
      this.buffers.delete(geometry);
    }
  }

  createBuffer(geometry: THREE.BufferGeometry) {
    const gl = this.gl;
    const webglBuffer = new PotreeWebGLBuffer();
    if (!geometry.attributes.position) return webglBuffer;
    webglBuffer.vao = gl.createVertexArray();
    webglBuffer.numElements = (geometry.attributes.position as THREE.BufferAttribute).count;

    gl.bindVertexArray(webglBuffer.vao);

    for (const attributeName in geometry.attributes) {
      const bufferAttribute = geometry.attributes[attributeName] as THREE.BufferAttribute;

      const vbo = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferData(gl.ARRAY_BUFFER, bufferAttribute.array, gl.STATIC_DRAW);

      const normalized = bufferAttribute.normalized;
      const type = this.glTypeMapping.get(bufferAttribute.array.constructor);

      if (attributeLocations[attributeName] === undefined) {
        //attributeLocation = attributeLocations["aExtra"];
      } else if (type !== undefined) {
        const attributeLocation = attributeLocations[attributeName].location;

        gl.vertexAttribPointer(attributeLocation, bufferAttribute.itemSize, type, normalized, 0, 0);
        gl.enableVertexAttribArray(attributeLocation);
      }

      webglBuffer.vbos.set(attributeName, {
        handle: vbo,
        name: attributeName,
        count: bufferAttribute.count,
        itemSize: bufferAttribute.itemSize,
        type: (geometry.attributes.position as THREE.BufferAttribute).array.constructor,
        version: 0,
      });
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);

    const disposeHandler = () => {
      this.deleteBuffer(geometry);
      geometry.removeEventListener('dispose', disposeHandler);
    };
    geometry.addEventListener('dispose', disposeHandler);

    return webglBuffer;
  }

  updateBuffer(geometry: THREE.BufferGeometry) {
    const gl = this.gl;

    const webglBuffer = this.buffers.get(geometry);
    if (!webglBuffer) return;

    gl.bindVertexArray(webglBuffer.vao);

    for (const attributeName in geometry.attributes) {
      const bufferAttribute = geometry.attributes[attributeName] as THREE.BufferAttribute;

      const normalized = bufferAttribute.normalized;
      const type = this.glTypeMapping.get(bufferAttribute.array.constructor);

      let vbo: globalThis.WebGLBuffer | null = null;
      if (!webglBuffer.vbos.has(attributeName)) {
        vbo = gl.createBuffer();

        webglBuffer.vbos.set(attributeName, {
          handle: vbo,
          name: attributeName,
          count: bufferAttribute.count,
          itemSize: bufferAttribute.itemSize,
          type: (geometry.attributes.position as THREE.BufferAttribute).array.constructor,
          version: bufferAttribute.version,
        });
      } else {
        const vboEntry = webglBuffer.vbos.get(attributeName);
        if (vboEntry) {
          vbo = vboEntry.handle;
          vboEntry.version = bufferAttribute.version;
        }
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferData(gl.ARRAY_BUFFER, bufferAttribute.array, gl.STATIC_DRAW);

      if (attributeLocations[attributeName] === undefined) {
        //attributeLocation = attributeLocations["aExtra"];
      } else if (type !== undefined) {
        const attributeLocation = attributeLocations[attributeName].location;

        gl.vertexAttribPointer(attributeLocation, bufferAttribute.itemSize, type, normalized, 0, 0);
        gl.enableVertexAttribArray(attributeLocation);
      }
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);
  }

  traverse(scene: THREE.Scene) {
    const octrees: PointCloudTree[] = [];

    const stack: THREE.Object3D[] = [scene];
    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) continue;

      if (node instanceof PointCloudTree) {
        octrees.push(node);
        continue;
      }

      const visibleChildren = node.children.filter((c) => c.visible);
      stack.push(...visibleChildren);
    }

    const result = {
      octrees: octrees,
    };

    return result;
  }

  renderNodes(octree: PotreeOctree, nodes: PotreeOctreeNode[], visibilityTextureData: { offsets: Map<PotreeOctreeNode, number> } | null, camera: THREE.Camera, target: THREE.WebGLRenderTarget | null, shader: Shader, params: RenderParams) {
    if (measureTimings) performance.mark('renderNodes-start');

    const gl = this.gl;

    const material = params.material ? params.material : octree.material;
    const shadowMaps = params.shadowMaps == null ? [] : params.shadowMaps;
    let view = camera.matrixWorldInverse;

    if (params.viewOverride) {
      view = params.viewOverride;
    }

    const worldView = new THREE.Matrix4();

    const mat4holder = new Float32Array(16);

    let i = 0;
    for (const node of nodes) {
      if (debug.allowedNodes !== undefined) {
        if (!debug.allowedNodes.includes(node.name)) {
          continue;
        }
      }

      const world = node.sceneNode.matrixWorld;
      worldView.multiplyMatrices(view, world);

      if (visibilityTextureData) {
        const vnStart = visibilityTextureData.offsets.get(node) ?? 0;
        shader.setUniform1f('uVNStart', vnStart);
      }

      const level = node.getLevel();

      if (node.debug) {
        shader.setUniform('uDebug', true);
      } else {
        shader.setUniform('uDebug', false);
      }

      // TODO consider passing matrices in an array to avoid uniformMatrix4fv overhead
      const lModel = shader.uniformLocations['modelMatrix'];
      if (lModel) {
        mat4holder.set(world.elements);
        gl.uniformMatrix4fv(lModel, false, mat4holder);
      }

      const lModelView = shader.uniformLocations['modelViewMatrix'];
      // faster than set in chrome 63
      for (let j = 0; j < 16; j++) {
        mat4holder[j] = worldView.elements[j];
      }
      gl.uniformMatrix4fv(lModelView, false, mat4holder);

      // Clip Polygons
      if (material.clipPolygons && material.clipPolygons.length > 0) {
        const clipPolygonVCount: number[] = [];
        const worldViewProjMatrices: THREE.Matrix4[] = [];

        for (const clipPolygon of material.clipPolygons) {
          const clipView = clipPolygon.viewMatrix;
          const proj = clipPolygon.projMatrix;

          const worldViewProj = proj.clone().multiply(clipView).multiply(world);

          clipPolygonVCount.push(clipPolygon.markers.length);
          worldViewProjMatrices.push(worldViewProj);
        }

        const flattenedMatrices = ([] as number[]).concat(...worldViewProjMatrices.map((m) => Array.from(m.elements)));

        const flattenedVertices = new Array(8 * 3 * material.clipPolygons.length);
        for (let ci = 0; ci < material.clipPolygons.length; ci++) {
          const clipPolygon = material.clipPolygons[ci];
          for (let j = 0; j < clipPolygon.markers.length; j++) {
            flattenedVertices[ci * 24 + (j * 3 + 0)] = clipPolygon.markers[j].position.x;
            flattenedVertices[ci * 24 + (j * 3 + 1)] = clipPolygon.markers[j].position.y;
            flattenedVertices[ci * 24 + (j * 3 + 2)] = clipPolygon.markers[j].position.z;
          }
        }

        const lClipPolygonVCount = shader.uniformLocations['uClipPolygonVCount[0]'];
        gl.uniform1iv(lClipPolygonVCount, clipPolygonVCount);

        const lClipPolygonVP = shader.uniformLocations['uClipPolygonWVP[0]'];
        gl.uniformMatrix4fv(lClipPolygonVP, false, flattenedMatrices);

        const lClipPolygons = shader.uniformLocations['uClipPolygonVertices[0]'];
        gl.uniform3fv(lClipPolygons, flattenedVertices);
      }

      shader.setUniform1f('uLevel', level as number);
      shader.setUniform1f('uNodeSpacing', node.geometryNode.estimatedSpacing || 0);

      shader.setUniform1f('uPCIndex', i);

      if (shadowMaps.length > 0) {
        const lShadowMap = shader.uniformLocations['uShadowMap[0]'];

        shader.setUniform3f('uShadowColor', material.uniforms.uShadowColor.value as number[]);

        const bindingStart = 5;
        const bindingPoints = new Array(shadowMaps.length).fill(bindingStart).map((a: number, si: number) => a + si);
        gl.uniform1iv(lShadowMap, bindingPoints);

        for (let si = 0; si < shadowMaps.length; si++) {
          const shadowMap = shadowMaps[si];
          const bindingPoint = bindingPoints[si];
          const glTexture = (this.threeRenderer.properties.get(shadowMap.target.texture) as Record<string, unknown>).__webglTexture as globalThis.WebGLTexture;

          gl.activeTexture(gl.TEXTURE0 + bindingPoint);
          gl.bindTexture(gl.TEXTURE_2D, glTexture);
        }

        {
          const worldViewMatrices = shadowMaps.map((sm) => sm.camera.matrixWorldInverse).map((smView) => new THREE.Matrix4().multiplyMatrices(smView, world));

          const flattenedMatrices = ([] as number[]).concat(...worldViewMatrices.map((c) => Array.from(c.elements)));
          const lWorldView = shader.uniformLocations['uShadowWorldView[0]'];
          gl.uniformMatrix4fv(lWorldView, false, flattenedMatrices);
        }

        {
          const flattenedMatrices = ([] as number[]).concat(...shadowMaps.map((sm) => Array.from(sm.camera.projectionMatrix.elements)));
          const lProj = shader.uniformLocations['uShadowProj[0]'];
          gl.uniformMatrix4fv(lProj, false, flattenedMatrices);
        }
      }

      const geometry = node.geometryNode.geometry;
      if (!geometry) {
        i++;
        continue;
      }

      if (geometry.attributes['gps-time']) {
        const attGPS = octree.getAttribute('gps-time');

        if (attGPS) {
          const initialRange = attGPS.initialRange;
          const initialRangeSize = initialRange[1] - initialRange[0];

          const globalRange = attGPS.range;
          const globalRangeSize = globalRange[1] - globalRange[0];

          let scale = initialRangeSize / globalRangeSize;
          let offset = -(globalRange[0] - initialRange[0]) / initialRangeSize;

          scale = Number.isNaN(scale) ? 1 : scale;
          offset = Number.isNaN(offset) ? 0 : offset;

          shader.setUniform1f('uGpsScale', scale);
          shader.setUniform1f('uGpsOffset', offset);

          const uFilterGPSTimeClipRange = material.uniforms.uFilterGPSTimeClipRange.value as number[];

          const normalizedClipRange = [(uFilterGPSTimeClipRange[0] - globalRange[0]) / globalRangeSize, (uFilterGPSTimeClipRange[1] - globalRange[0]) / globalRangeSize];

          shader.setUniform2f('uFilterGPSTimeClipRange', normalizedClipRange);
        }
      }

      {
        const uFilterReturnNumberRange = material.uniforms.uFilterReturnNumberRange.value as number[];
        const uFilterNumberOfReturnsRange = material.uniforms.uFilterNumberOfReturnsRange.value as number[];
        const uFilterPointSourceIDClipRange = material.uniforms.uFilterPointSourceIDClipRange.value as number[];

        shader.setUniform2f('uFilterReturnNumberRange', uFilterReturnNumberRange);
        shader.setUniform2f('uFilterNumberOfReturnsRange', uFilterNumberOfReturnsRange);
        shader.setUniform2f('uFilterPointSourceIDClipRange', uFilterPointSourceIDClipRange);
      }

      let webglBuffer: PotreeWebGLBuffer;
      if (!this.buffers.has(geometry)) {
        webglBuffer = this.createBuffer(geometry);
        this.buffers.set(geometry, webglBuffer);
      } else {
        const existing = this.buffers.get(geometry);
        if (!existing) continue;
        webglBuffer = existing;
        for (const attributeName in geometry.attributes) {
          const attribute = geometry.attributes[attributeName] as THREE.BufferAttribute;

          const vboEntry = webglBuffer.vbos.get(attributeName);
          if (vboEntry && attribute.version > vboEntry.version) {
            this.updateBuffer(geometry);
          }
        }
      }

      gl.bindVertexArray(webglBuffer.vao);

      const isExtraAttribute = attributeLocations[material.activeAttributeName || ''] === undefined && Object.keys(geometry.attributes).includes(material.activeAttributeName || '');

      if (isExtraAttribute) {
        const extraAttributeLocation = attributeLocations['aExtra'].location;

        for (const attributeName in geometry.attributes) {
          const vbo = webglBuffer.vbos.get(attributeName);
          if (!vbo) continue;
          gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle);
          gl.disableVertexAttribArray(extraAttributeLocation);
        }

        const attName = material.activeAttributeName || '';
        const bufferAttribute = geometry.attributes[attName] as THREE.BufferAttribute | undefined;
        const vbo = webglBuffer.vbos.get(attName);

        if (bufferAttribute !== undefined && vbo !== undefined) {
          const type = this.glTypeMapping.get(bufferAttribute.array.constructor);
          const normalized = bufferAttribute.normalized;
          if (vbo) gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle);
          if (type !== undefined) {
            gl.vertexAttribPointer(extraAttributeLocation, bufferAttribute.itemSize, type, normalized, 0, 0);
            gl.enableVertexAttribArray(extraAttributeLocation);
          }
        }

        {
          const attExtra = octree.pcoGeometry.pointAttributes.attributes.find((a) => a.name === attName);

          let range: [number, number] | null = material.getRange(attName);
          if (!range && attExtra) {
            range = attExtra.range;
          }

          if (!range) {
            range = [0, 1];
          }

          const initialRange = attExtra ? attExtra.initialRange : [0, 1];
          const initialRangeSize = initialRange[1] - initialRange[0];

          const globalRange = range;
          const globalRangeSize = globalRange[1] - globalRange[0];

          let scale = initialRangeSize / globalRangeSize;
          let offset = -(globalRange[0] - initialRange[0]) / initialRangeSize;

          scale = Number.isNaN(scale) ? 1 : scale;
          offset = Number.isNaN(offset) ? 0 : offset;

          shader.setUniform1f('uExtraScale', scale);
          shader.setUniform1f('uExtraOffset', offset);
        }
      } else {
        for (const attributeName in geometry.attributes) {
          const bufferAttribute = geometry.attributes[attributeName] as THREE.BufferAttribute;
          const vbo = webglBuffer.vbos.get(attributeName);

          if (attributeLocations[attributeName] !== undefined) {
            const attrLocation = attributeLocations[attributeName].location;

            const type = this.glTypeMapping.get(bufferAttribute.array.constructor);
            const normalized = bufferAttribute.normalized;
            if (vbo) gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle);
            if (type !== undefined) {
              gl.vertexAttribPointer(attrLocation, bufferAttribute.itemSize, type, normalized, 0, 0);
              gl.enableVertexAttribArray(attrLocation);
            }
          }
        }
      }

      // Pythagoras
      if (!node.refinedByAllChildren) {
        const numPoints = webglBuffer.numElements;
        gl.drawArrays(gl.POINTS, 0, numPoints);
      }

      i++;
    }

    gl.bindVertexArray(null);

    if (measureTimings) {
      performance.mark('renderNodes-end');
      performance.measure('render.renderNodes', 'renderNodes-start', 'renderNodes-end');
    }
  }

  renderOctree(octree: PotreeOctree, nodes: PotreeOctreeNode[], camera: THREE.Camera, target: THREE.WebGLRenderTarget | null, params: RenderParams = {}) {
    const gl = this.gl;

    const material = params.material ? params.material : octree.material;
    const shadowMaps = params.shadowMaps == null ? [] : params.shadowMaps;
    let view = camera.matrixWorldInverse;
    let viewInv = camera.matrixWorld;

    if (params.viewOverride) {
      view = params.viewOverride;
      viewInv = view.clone().invert();
    }

    const proj = camera.projectionMatrix;
    const projInv = proj.clone().invert();

    let shader: Shader | null = null;
    let visibilityTextureData: { data: Uint8Array; offsets: Map<PotreeOctreeNode, number> } | null = null;

    let currentTextureBindingPoint = 0;

    if (material.pointSizeType >= 0) {
      if (material.pointSizeType === PointSizeType.ADAPTIVE || material.activeAttributeName === 'level of detail') {
        const vnNodes = params.vnTextureNodes != null ? params.vnTextureNodes : nodes;
        visibilityTextureData = octree.computeVisibilityTextureData(vnNodes, camera);

        const vnt = material.visibleNodesTexture;
        const data = vnt.image.data as Uint8Array;
        // Texture holds 2048 nodes (shader hardcodes the width); drop the deepest nodes past that
        data.set(visibilityTextureData.data.subarray(0, data.length));
        vnt.needsUpdate = true;
      }
    }

    // UPDATE SHADER AND TEXTURES
    if (!this.shaders.has(material)) {
      const [vs, fs] = [material.vertexShader, material.fragmentShader];
      const newShader = new Shader(gl, 'pointcloud', vs, fs);

      this.shaders.set(material, newShader);
    }

    shader = this.shaders.get(material) ?? null;
    if (!shader) return;

    let [vs, fs] = [material.vertexShader, material.fragmentShader];

    const numSnapshots = material.snapEnabled ? material.numSnapshots || 0 : 0;
    const numClipBoxes = material.clipBoxes && material.clipBoxes.length ? material.clipBoxes.length : 0;
    const numClipSpheres = material.clipSpheres && material.clipSpheres.length ? material.clipSpheres.length : 0;
    const numClipPolygons = material.clipPolygons && material.clipPolygons.length ? material.clipPolygons.length : 0;
    const numClipPlanes = material.clipPlanes && material.clipPlanes.length ? material.clipPlanes.length : 0;

    const defines = [
      `#define num_shadowmaps ${shadowMaps.length}`,
      `#define num_snapshots ${numSnapshots}`,
      `#define num_clipboxes ${numClipBoxes}`,
      `#define num_clipspheres ${numClipSpheres}`,
      `#define num_clippolygons ${numClipPolygons}`,
      `#define num_clipPlanes ${numClipPlanes}`,
    ];

    if (octree.pcoGeometry.root.isLoaded()) {
      const rootGeometry = octree.pcoGeometry.root.geometry;
      if (rootGeometry) {
        const attributes = rootGeometry.attributes;

        if (attributes['gps-time']) {
          defines.push('#define clip_gps_enabled');
        }

        if (attributes['return number']) {
          defines.push('#define clip_return_number_enabled');
        }

        if (attributes['number of returns']) {
          defines.push('#define clip_number_of_returns_enabled');
        }

        if (attributes['source id'] || attributes['point source id']) {
          defines.push('#define clip_point_source_id_enabled');
        }
      }
    }

    const definesString = defines.join('\n');

    const vsVersionIndex = vs.indexOf('#version ');
    const fsVersionIndex = fs.indexOf('#version ');

    if (vsVersionIndex >= 0) {
      vs = vs.replace(/(#version .*)/, `$1\n${definesString}`);
    } else {
      vs = `${definesString}\n${vs}`;
    }

    if (fsVersionIndex >= 0) {
      fs = fs.replace(/(#version .*)/, `$1\n${definesString}`);
    } else {
      fs = `${definesString}\n${fs}`;
    }

    shader.update(vs, fs);

    material.needsUpdate = false;

    for (const uniformName of Object.keys(material.uniforms)) {
      const uniform = material.uniforms[uniformName];

      if (uniform.type === 't') {
        const texture = uniform.value as THREE.Texture | null;

        if (!texture) {
          continue;
        }

        if (!this.textures.has(texture)) {
          const webglTexture = new PotreeWebGLTexture(gl, texture);

          this.textures.set(texture, webglTexture);
        }

        const webGLTexture = this.textures.get(texture);
        if (webGLTexture) {
          webGLTexture.update();
        }
      }
    }

    gl.useProgram(shader.program);

    let transparent = false;
    if (params.transparent !== undefined) {
      transparent = params.transparent && material.opacity < 1;
    } else {
      transparent = material.opacity < 1 || !!material.densityMode;
    }

    if (transparent) {
      gl.enable(gl.BLEND);
      if (material.densityMode) {
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ZERO, gl.ONE);
      } else {
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
      }
      gl.depthMask(false);
      gl.disable(gl.DEPTH_TEST);
    } else {
      gl.disable(gl.BLEND);
      gl.depthMask(true);
      gl.enable(gl.DEPTH_TEST);
    }

    if (params.blendFunc !== undefined) {
      gl.enable(gl.BLEND);
      gl.blendFunc(params.blendFunc[0], params.blendFunc[1]);
    }

    if (params.depthTest !== undefined) {
      if (params.depthTest === true) {
        gl.enable(gl.DEPTH_TEST);
      } else {
        gl.disable(gl.DEPTH_TEST);
      }
    }

    if (params.depthWrite !== undefined) {
      if (params.depthWrite === true) {
        gl.depthMask(true);
      } else {
        gl.depthMask(false);
      }
    }

    {
      // UPDATE UNIFORMS
      shader.setUniformMatrix4('projectionMatrix', proj);
      shader.setUniformMatrix4('viewMatrix', view);
      shader.setUniformMatrix4('uViewInv', viewInv);
      shader.setUniformMatrix4('uProjInv', projInv);

      const screenWidth = target ? target.width : material.screenWidth;
      const screenHeight = target ? target.height : material.screenHeight;

      shader.setUniform1f('uScreenWidth', screenWidth);
      shader.setUniform1f('uScreenHeight', screenHeight);
      shader.setUniform1f('fov', (Math.PI * (camera as THREE.PerspectiveCamera).fov) / 180);
      shader.setUniform1f('near', (camera as THREE.PerspectiveCamera).near);
      shader.setUniform1f('far', (camera as THREE.PerspectiveCamera).far);

      if (camera instanceof THREE.OrthographicCamera) {
        shader.setUniform('uUseOrthographicCamera', true);
        shader.setUniform('uOrthoWidth', camera.right - camera.left);
        shader.setUniform('uOrthoHeight', camera.top - camera.bottom);
      } else {
        shader.setUniform('uUseOrthographicCamera', false);
      }

      let isolateAnything = false;
      let grayscaleAnything = false;

      if (material.clipBoxes && material.clipBoxes.length > 0) {
        const lClipBoxes = shader.uniformLocations['clipBoxes[0]'];
        gl.uniformMatrix4fv(lClipBoxes, false, material.uniforms.clipBoxes.value as Float32Array);

        const clipTasks = material.clipBoxes.map((box) => box.box.getActualClipTask());
        const lClipTasks = shader.uniformLocations['clipTasks[0]'];
        gl.uniform1iv(lClipTasks, clipTasks);

        const boxColors = material.clipBoxes
          .map((box) => {
            const srgbColor = box.box.color.clone().convertLinearToSRGB();
            return [srgbColor.r, srgbColor.g, srgbColor.b];
          })
          .flat();
        const lBoxColors = shader.uniformLocations['boxColors[0]'];
        gl.uniform3fv(lBoxColors, boxColors);

        const boxUseIntersection = material.clipBoxes.map((box) => (box.box.useIntersection ? 1 : 0));
        const lBoxUseIntersection = shader.uniformLocations['boxUseIntersection[0]'];
        gl.uniform1iv(lBoxUseIntersection, boxUseIntersection);

        const hasUnionIsolateBoxes = material.clipBoxes.some((box, bi) => clipTasks[bi] === ClipTask.SHOW_INSIDE && !box.box.useIntersection);
        isolateAnything = isolateAnything || hasUnionIsolateBoxes;
        grayscaleAnything = grayscaleAnything || clipTasks.some((clipTask) => clipTask === ClipTask.GRAYSCALE);
      }

      if (material.clipPlanes && material.clipPlanes.length > 0) {
        let sortedClipPlanes = material.clipPlanes;

        if (params.orderedIdentifiers && params.orderedIdentifiers.length > 0) {
          const orderMap = new Map<string, number>();
          params.orderedIdentifiers.forEach(({ identifier, index }) => {
            orderMap.set(identifier, index);
          });

          sortedClipPlanes = material.clipPlanes.sort((a, b) => {
            const orderA = orderMap.get(a.plane.identifier || '') ?? Number.MAX_SAFE_INTEGER;
            const orderB = orderMap.get(b.plane.identifier || '') ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });
        }

        const sortedPlaneMatrices = new Float32Array(sortedClipPlanes.length * 16);
        for (let pi = 0; pi < sortedClipPlanes.length; pi++) {
          sortedPlaneMatrices.set(sortedClipPlanes[pi].inverse.elements, pi * 16);
        }

        const lPlanes = shader.uniformLocations['clipPlanes[0]'];
        gl.uniformMatrix4fv(lPlanes, false, sortedPlaneMatrices);

        const planeClipTasks = sortedClipPlanes.map((plane) => plane.plane.actualClipTask);
        const lplaneClipTasks = shader.uniformLocations['planeClipTasks[0]'];
        gl.uniform1iv(lplaneClipTasks, planeClipTasks);

        const planeTolerances = sortedClipPlanes.map((plane) => plane.plane.tolerance);
        const lPlaneTolerances = shader.uniformLocations['planeTolerances[0]'];
        gl.uniform1fv(lPlaneTolerances, planeTolerances);

        const gradientMap: Record<string, number> = {
          SPECTRAL: 0,
          PLASMA: 1,
          YELLOW_GREEN: 2,
          VIRIDIS: 3,
          INFERNO: 4,
          GRAYSCALE: 5,
          TURBO: 6,
          RAINBOW: 7,
          CONTOUR: 8,
          PURE_PURPLE: 9,
        };
        const planeGradients = sortedClipPlanes.map((plane) => gradientMap[plane.plane.gradient] || 0);
        const lPlaneGradients = shader.uniformLocations['planeGradients[0]'];
        gl.uniform1iv(lPlaneGradients, planeGradients);

        const planeHeatmapIsolate = sortedClipPlanes.map((plane) => plane.plane.actualClipTask === ClipTask.HEATMAP && !!plane.plane.heatmapIsolate);
        const lPlaneHeatmapIsolate = shader.uniformLocations['planeHeatmapIsolate[0]'];
        gl.uniform1iv(
          lPlaneHeatmapIsolate,
          planeHeatmapIsolate.map((v) => (v ? 1 : 0)),
        );

        const planeUseIntersection = sortedClipPlanes.map((plane) => (plane.plane.useIntersection ? 1 : 0));
        const lPlaneUseIntersection = shader.uniformLocations['planeUseIntersection[0]'];
        gl.uniform1iv(lPlaneUseIntersection, planeUseIntersection);

        const hasUnionIsolatePlanes = sortedClipPlanes.some((plane) => plane.plane.actualClipTask === ClipTask.SHOW_INSIDE && !plane.plane.useIntersection);
        isolateAnything = isolateAnything || hasUnionIsolatePlanes || planeHeatmapIsolate.some((isolate) => isolate === true);
        grayscaleAnything = grayscaleAnything || planeClipTasks.some((task) => task === ClipTask.GRAYSCALE);
      }

      if (material.clipSpheres && material.clipSpheres.length > 0) {
        // Compose each sphere's world→sphere-local inverse with THIS view's
        // camera, so the shader tests view-space positions (camera-relative
        // numbers — no UTM-scale fp32 precision loss, and a camera-following
        // sphere needs no material updates at all).
        const viewToWorld = camera.matrixWorld;
        const flattenedMatrices: number[] = [];
        for (const clipSphere of material.clipSpheres) {
          const viewToClip = new THREE.Matrix4().multiplyMatrices(clipSphere.inverse, viewToWorld);
          flattenedMatrices.push(...viewToClip.elements);
        }
        const lClipSpheres = shader.uniformLocations['uClipSpheres[0]'];
        gl.uniformMatrix4fv(lClipSpheres, false, flattenedMatrices);

        const sphereTasks = material.clipSpheres.map((clipSphere) => clipSphere.sphere.getActualClipTask());
        const lSphereTasks = shader.uniformLocations['uClipSphereTasks[0]'];
        gl.uniform1iv(lSphereTasks, sphereTasks);

        // A union-isolating sphere clips everything outside it, like union
        // isolation boxes/planes do.
        isolateAnything = isolateAnything || sphereTasks.some((task) => task === ClipTask.SHOW_INSIDE);
      }

      shader.setUniform1f('size', material.size);
      shader.setUniform1f('maxSize', material.uniforms.maxSize.value as number);
      shader.setUniform1f('minSize', material.uniforms.minSize.value as number);

      shader.setUniform1f('uOctreeSpacing', material.spacing);
      shader.setUniform('uOctreeSize', material.uniforms.octreeSize.value as number);

      shader.setUniform3f('uColor', material.color.toArray());
      shader.setUniform1f('uOpacity', material.opacity);
      shader.setUniform1f('uDensityAlpha', material.densityAlpha);

      shader.setUniform2f('elevationRange', material.elevationRange);
      shader.setUniform2f('intensityRange', material.intensityRange);

      shader.setUniform3f('uIntensity_gbc', [material.intensityGamma, material.intensityBrightness, material.intensityContrast]);

      shader.setUniform3f('uRGB_gbc', [material.rgbGamma, material.rgbBrightness, material.rgbContrast]);

      shader.setUniform1f('uTransition', material.transition);
      shader.setUniform1f('wRGB', material.weightRGB);
      shader.setUniform1f('wIntensity', material.weightIntensity);
      shader.setUniform1f('wElevation', material.weightElevation);
      shader.setUniform1f('wClassification', material.weightClassification);
      shader.setUniform1f('wReturnNumber', material.weightReturnNumber);
      shader.setUniform1f('wSourceID', material.weightSourceID);

      shader.setUniform('backfaceCulling', material.uniforms.backfaceCulling.value as boolean);

      shader.setUniform3f('uEyeMin', material.uniforms.uEyeMin.value as number[]);
      shader.setUniform3f('uEyeMax', material.uniforms.uEyeMax.value as number[]);

      const vnWebGLTexture = this.textures.get(material.visibleNodesTexture);
      if (vnWebGLTexture) {
        shader.setUniform1i('visibleNodesTexture', currentTextureBindingPoint);
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(vnWebGLTexture.target, vnWebGLTexture.id);
        currentTextureBindingPoint++;
      }

      // Handle gradient texture with proper caching
      if (!this.textures.has(material.gradientTexture)) {
        const webglTexture = new PotreeWebGLTexture(gl, material.gradientTexture);
        this.textures.set(material.gradientTexture, webglTexture);
      }

      const gradientTexture = this.textures.get(material.gradientTexture);
      if (gradientTexture) {
        gradientTexture.update();
        shader.setUniform1i('gradient', currentTextureBindingPoint);
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(gradientTexture.target, gradientTexture.id);
        currentTextureBindingPoint++;
      }

      // Set all gradient texture uniforms for heatmap support
      if (material.gradientTextures) {
        const gradientTextureKeys = [
          'gradient_spectral',
          'gradient_plasma',
          'gradient_yellow_green',
          'gradient_viridis',
          'gradient_inferno',
          'gradient_grayscale',
          'gradient_turbo',
          'gradient_rainbow',
          'gradient_contour',
          'gradient_PURE_PURPLE',
        ];

        for (const key of gradientTextureKeys) {
          const texture = material.gradientTextures[key];
          if (!this.textures.has(texture)) {
            const webglTexture = new PotreeWebGLTexture(gl, texture);
            this.textures.set(texture, webglTexture);
          }
        }

        for (const key of gradientTextureKeys) {
          const tex = this.textures.get(material.gradientTextures[key]);
          if (!tex) continue;
          shader.setUniform1i(key, currentTextureBindingPoint);
          gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
          gl.bindTexture(tex.target, tex.id);
          currentTextureBindingPoint++;
        }
      }

      if (gradientTexture) {
        const repeat = material.elevationGradientRepeat;
        if (repeat === ElevationGradientRepeat.REPEAT) {
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.REPEAT);
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.REPEAT);
        } else if (repeat === ElevationGradientRepeat.MIRRORED_REPEAT) {
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT);
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT);
        } else {
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        }
        currentTextureBindingPoint++;
      }

      const classificationTexture = this.textures.get(material.classificationTexture);
      if (classificationTexture) {
        shader.setUniform1i('classificationLUT', currentTextureBindingPoint);
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(classificationTexture.target, classificationTexture.id);
        currentTextureBindingPoint++;
      }

      const matcapTexture = this.textures.get(material.matcapTexture);
      if (matcapTexture) {
        shader.setUniform1i('matcapTextureUniform', currentTextureBindingPoint);
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(matcapTexture.target, matcapTexture.id);
        currentTextureBindingPoint++;
      }

      // WallSketch edge texture binding for per-edge distance calculation
      if (material.uniforms.wallSketchSDFEnabled?.value && material.uniforms.wallSketchEdgeTexture?.value) {
        const edgeTexture = material.uniforms.wallSketchEdgeTexture.value as THREE.Texture;

        if (!this.textures.has(edgeTexture)) {
          const webglTexture = new PotreeWebGLTexture(gl, edgeTexture);
          this.textures.set(edgeTexture, webglTexture);
        }

        const webGLEdgeTexture = this.textures.get(edgeTexture);
        if (!webGLEdgeTexture) return;
        webGLEdgeTexture.update();

        shader.setUniform1i('wallSketchEdgeTexture', currentTextureBindingPoint);
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(webGLEdgeTexture.target, webGLEdgeTexture.id);
        currentTextureBindingPoint++;

        shader.setUniform1i('wallSketchSDFEnabled', 1);
        shader.setUniform1i('wallSketchEdgeCount', material.uniforms.wallSketchEdgeCount.value as number);
        shader.setUniform1f('wallSketchSDFMaxDistance', material.uniforms.wallSketchSDFMaxDistance.value as number);
        shader.setUniform1i('wallSketchSDFGradient', material.uniforms.wallSketchSDFGradient.value as number);
        shader.setUniform1f('wallSketchSDFFloorHeight', material.uniforms.wallSketchSDFFloorHeight.value as number);
        shader.setUniform1f('wallSketchSDFHeightTolerance', material.uniforms.wallSketchSDFHeightTolerance.value as number);
        shader.setUniform1i('wallSketchSDFClipMethod', material.uniforms.wallSketchSDFClipMethod.value as number);
        shader.setUniform1f('wallSketchSDFClipTolerance', material.uniforms.wallSketchSDFClipTolerance.value as number);
      }

      // Floor 3D mesh edge texture — parallel SDF path. Bound separately
      // from the skeleton's so both can be active on the same point cloud.
      // Potree uses its own custom GL pipeline (this file) rather than
      // three.js' standard material upload — uniform values set on the
      // material from JS aren't automatically uploaded, so we have to
      // push them per-frame here, mirroring the skeleton block above.
      if (material.uniforms.floorMesh3DSDFEnabled?.value && material.uniforms.floorMesh3DEdgeTexture?.value) {
        const edgeTexture = material.uniforms.floorMesh3DEdgeTexture.value as THREE.Texture;

        if (!this.textures.has(edgeTexture)) {
          const webglTexture = new PotreeWebGLTexture(gl, edgeTexture);
          this.textures.set(edgeTexture, webglTexture);
        }

        const webGLEdgeTexture = this.textures.get(edgeTexture);
        if (!webGLEdgeTexture) return;
        webGLEdgeTexture.update();

        shader.setUniform1i('floorMesh3DEdgeTexture', currentTextureBindingPoint);
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(webGLEdgeTexture.target, webGLEdgeTexture.id);
        currentTextureBindingPoint++;

        shader.setUniform1i('floorMesh3DSDFEnabled', 1);
        shader.setUniform1i('floorMesh3DEdgeCount', material.uniforms.floorMesh3DEdgeCount.value as number);
        shader.setUniform1f('floorMesh3DSDFMaxDistance', material.uniforms.floorMesh3DSDFMaxDistance.value as number);
        shader.setUniform1i('floorMesh3DSDFGradient', material.uniforms.floorMesh3DSDFGradient.value as number);
        shader.setUniform1f('floorMesh3DSDFFloorHeight', material.uniforms.floorMesh3DSDFFloorHeight.value as number);
        shader.setUniform1f('floorMesh3DSDFHeightTolerance', material.uniforms.floorMesh3DSDFHeightTolerance.value as number);
        shader.setUniform1i('floorMesh3DSDFClipMethod', material.uniforms.floorMesh3DSDFClipMethod.value as number);
        shader.setUniform1f('floorMesh3DSDFClipTolerance', material.uniforms.floorMesh3DSDFClipTolerance.value as number);
      }

      if (material.snapEnabled === true) {
        {
          const lSnapshot = shader.uniformLocations['uSnapshot[0]'];
          const lSnapshotDepth = shader.uniformLocations['uSnapshotDepth[0]'];

          const bindingStart = currentTextureBindingPoint;
          const lSnapshotBindingPoints = new Array(5).fill(bindingStart).map((a: number, si: number) => a + si);
          const lSnapshotDepthBindingPoints = new Array(5).fill(1 + Math.max(...lSnapshotBindingPoints)).map((a: number, si: number) => a + si);
          currentTextureBindingPoint = 1 + Math.max(...lSnapshotDepthBindingPoints);

          gl.uniform1iv(lSnapshot, lSnapshotBindingPoints);
          gl.uniform1iv(lSnapshotDepth, lSnapshotDepthBindingPoints);

          for (let si = 0; si < 5; si++) {
            const texture = (material.uniforms[`uSnapshot`].value as THREE.Texture[])[si];
            const textureDepth = (material.uniforms[`uSnapshotDepth`].value as THREE.Texture[])[si];

            if (!texture) {
              break;
            }

            const snapTexture = (this.threeRenderer.properties.get(texture) as Record<string, unknown>).__webglTexture as globalThis.WebGLTexture;
            const snapTextureDepth = (this.threeRenderer.properties.get(textureDepth) as Record<string, unknown>).__webglTexture as globalThis.WebGLTexture;

            const bindingPoint = lSnapshotBindingPoints[si];
            const depthBindingPoint = lSnapshotDepthBindingPoints[si];

            gl.activeTexture(gl.TEXTURE0 + bindingPoint);
            gl.bindTexture(gl.TEXTURE_2D, snapTexture);

            gl.activeTexture(gl.TEXTURE0 + depthBindingPoint);
            gl.bindTexture(gl.TEXTURE_2D, snapTextureDepth);
          }
        }

        {
          const flattenedMatrices = ([] as number[]).concat(...(material.uniforms.uSnapView.value as THREE.Matrix4[]).map((c) => Array.from(c.elements)));
          const lSnapView = shader.uniformLocations['uSnapView[0]'];
          gl.uniformMatrix4fv(lSnapView, false, flattenedMatrices);
        }
        {
          const flattenedMatrices = ([] as number[]).concat(...(material.uniforms.uSnapProj.value as THREE.Matrix4[]).map((c) => Array.from(c.elements)));
          const lSnapProj = shader.uniformLocations['uSnapProj[0]'];
          gl.uniformMatrix4fv(lSnapProj, false, flattenedMatrices);
        }
        {
          const flattenedMatrices = ([] as number[]).concat(...(material.uniforms.uSnapProjInv.value as THREE.Matrix4[]).map((c) => Array.from(c.elements)));
          const lSnapProjInv = shader.uniformLocations['uSnapProjInv[0]'];
          gl.uniformMatrix4fv(lSnapProjInv, false, flattenedMatrices);
        }
        {
          const flattenedMatrices = ([] as number[]).concat(...(material.uniforms.uSnapViewInv.value as THREE.Matrix4[]).map((c) => Array.from(c.elements)));
          const lSnapViewInv = shader.uniformLocations['uSnapViewInv[0]'];
          gl.uniformMatrix4fv(lSnapViewInv, false, flattenedMatrices);
        }
      }

      {
        const sources = material.sources;
        const nrSources = sources ? Object.keys(sources).length : 0;
        const sourceData = new Float32Array(nrSources * 4);

        if (sources) {
          for (const source in sources) {
            const srcIdx = Number(source);
            const offset = srcIdx * 4;
            sourceData[offset + 0] = sources[source].color[0];
            sourceData[offset + 1] = sources[source].color[1];
            sourceData[offset + 2] = sources[source].color[2];
            sourceData[offset + 3] = sources[source].visible ? 1.0 : 0.0;
          }
        }

        const sourceTexture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(gl.TEXTURE_2D, sourceTexture);

        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, nrSources, 1, 0, gl.RGBA, gl.FLOAT, sourceData);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(gl.TEXTURE_2D, sourceTexture);

        const ltex = shader.uniformLocations['sourceTexture'];
        gl.uniform1i(ltex, currentTextureBindingPoint);

        currentTextureBindingPoint++;

        const lNrSources = shader.uniformLocations['nrSources'];
        gl.uniform1f(lNrSources, nrSources);
      }

      {
        const segments = material.clusteredSegments;
        const maxIndex = Math.max(...segments.map((segment) => segment.identifier), 1);

        const textureHeight = 4096;
        const textureWidth = Math.ceil(maxIndex / textureHeight);
        const vecsPerSegment = 2;
        const floatsPerSegment = vecsPerSegment * 4;
        const segmentsInTexture = textureHeight * textureWidth;
        const segData = new Float32Array(segmentsInTexture * floatsPerSegment);

        for (let si = 0; si < segmentsInTexture; si++) {
          const offset = si * floatsPerSegment;
          segData[offset + 5] = 1.0;
          segData[offset + 7] = -1;
        }

        segments.forEach((segment) => {
          const rowOffset = (segment.identifier % textureHeight) * floatsPerSegment * textureWidth;
          const colOffset = Math.floor(segment.identifier / textureHeight) * floatsPerSegment;

          const srgbColor = segment.color.clone().convertLinearToSRGB();

          segData[rowOffset + colOffset + 0] = srgbColor.r;
          segData[rowOffset + colOffset + 1] = srgbColor.g;
          segData[rowOffset + colOffset + 2] = srgbColor.b;
          segData[rowOffset + colOffset + 3] = 0;

          segData[rowOffset + colOffset + 4] = 0;
          segData[rowOffset + colOffset + 5] = segment.visible ? 1.0 : 0.0;
          segData[rowOffset + colOffset + 6] = segment.clipTask;
          segData[rowOffset + colOffset + 7] = segment.classification;
        });

        const segTexture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(gl.TEXTURE_2D, segTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, vecsPerSegment * textureWidth, textureHeight, 0, gl.RGBA, gl.FLOAT, segData);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(gl.TEXTURE_2D, segTexture);

        const ltex = shader.uniformLocations['segmentDataTexture'];
        gl.uniform1i(ltex, currentTextureBindingPoint);

        currentTextureBindingPoint++;

        isolateAnything = isolateAnything || segments.some((segment) => segment.clipTask === ClipTask.SHOW_INSIDE);
        grayscaleAnything = grayscaleAnything || segments.some((segment) => segment.clipTask === ClipTask.GRAYSCALE);

        const lIsolate = shader.uniformLocations['isolateAnything'];
        gl.uniform1i(lIsolate, isolateAnything ? 1 : 0);

        const lGrayscale = shader.uniformLocations['grayscaleAnything'];
        gl.uniform1i(lGrayscale, grayscaleAnything ? 1 : 0);

        const lHeight = shader.uniformLocations['segmentDataTextureHeight'];
        gl.uniform1f(lHeight, textureHeight);

        const lWidth = shader.uniformLocations['segmentDataTextureWidth'];
        gl.uniform1f(lWidth, textureWidth);
      }
    }

    this.renderNodes(octree, nodes, visibilityTextureData, camera, target, shader, params);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
  }

  render(scene: THREE.Scene, camera: THREE.Camera, target: THREE.WebGLRenderTarget | null = null, params: RenderParams = {}) {
    const gl = this.gl;

    // PREPARE
    if (target != null) {
      this.threeRenderer.setRenderTarget(target);
    }

    const traversalResult = this.traverse(scene);

    // RENDER
    for (const octree of traversalResult.octrees) {
      const nodes = (octree as unknown as PotreeOctree).visibleNodes;
      this.renderOctree(octree as unknown as PotreeOctree, nodes, camera, target, params);
    }

    // CLEANUP
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);

    this.threeRenderer.resetState();
  }
}
